import dayjs from 'dayjs'
import {
    FilterDefinition,
    getUniqueFilterOptions,
    UNASSIGNED_FILTER_OPTION_ID,
    UNKNOWN_VALUE_LABEL,
} from './tableFilters'

export const createTextFieldFilter = <TRow>(
    id: string,
    label: string,
    getValue: (row: TRow) => string | undefined,
    vocabulary: (string | undefined)[] = []
): FilterDefinition<TRow> => ({
    id,
    label,
    getOptions: (rows) =>
        getUniqueFilterOptions(
            [...vocabulary, ...rows.map(getValue)].map((value) => ({
                id: value || UNASSIGNED_FILTER_OPTION_ID,
                label: value || UNKNOWN_VALUE_LABEL,
                order: value ? undefined : Number.MAX_SAFE_INTEGER,
            }))
        ),
    matches: (row, selectedOptionIds) => selectedOptionIds.includes(getValue(row) || UNASSIGNED_FILTER_OPTION_ID),
})

export const createBinaryFilter = <TRow>(
    id: string,
    label: string,
    onOption: { id: string; label: string },
    offOption: { id: string; label: string },
    isOn: (row: TRow) => boolean,
    defaultHidden = false
): FilterDefinition<TRow> => ({
    id,
    label,
    defaultHidden,
    getOptions: () => [
        { ...onOption, order: 0 },
        { ...offOption, order: 1 },
    ],
    matches: (row, selectedOptionIds) => selectedOptionIds.includes(isOn(row) ? onOption.id : offOption.id),
})

const DATE_BUCKETS = [
    { id: 'last7Days', label: 'Last 7 days', maxDaysAgo: 7 },
    { id: 'last30Days', label: 'Last 30 days', maxDaysAgo: 30 },
    { id: 'last90Days', label: 'Last 90 days', maxDaysAgo: 90 },
]

const MISSING_DATE_OPTION_ID = 'never'

const NO_DATE_OPTION_ID = 'noDate'

const getYear = (date?: Date | string | null) => {
    if (!date) return undefined

    const parsed = date instanceof Date ? date : new Date(date)

    return Number.isNaN(parsed.getTime()) ? undefined : parsed.getUTCFullYear()
}
const OVER_90_DAYS_OPTION_ID = 'over90Days'

const getDateBucketId = (date?: string | null, now: dayjs.Dayjs = dayjs()) => {
    if (!date) {
        return MISSING_DATE_OPTION_ID
    }

    const daysAgo = now.diff(dayjs(date), 'day')

    return DATE_BUCKETS.find((bucket) => daysAgo <= bucket.maxDaysAgo)?.id ?? OVER_90_DAYS_OPTION_ID
}

type CreateDateFilterArgs<TRow> = {
    id: string
    label: string
    getDate: (row: TRow) => string | null | undefined
    columnId?: string
    defaultHidden?: boolean
}

export const createDateFilter = <TRow>({
    id,
    label,
    getDate,
    columnId,
    defaultHidden,
}: CreateDateFilterArgs<TRow>): FilterDefinition<TRow> => ({
    id,
    label,
    columnId,
    defaultHidden,
    getOptions: () => [
        ...DATE_BUCKETS.map((bucket, index) => ({ id: bucket.id, label: bucket.label, order: index })),
        { id: OVER_90_DAYS_OPTION_ID, label: 'Over 90 days ago', order: DATE_BUCKETS.length },
        { id: MISSING_DATE_OPTION_ID, label: UNKNOWN_VALUE_LABEL, order: DATE_BUCKETS.length + 1 },
    ],
    matches: (row, selectedOptionIds) => selectedOptionIds.includes(getDateBucketId(getDate(row))),
})

export const createActivityFilter = <TRow extends { lastActivity?: string | null }>(): FilterDefinition<TRow> =>
    createDateFilter<TRow>({
        id: 'dateOfActivity',
        label: 'Last Activity',
        columnId: 'lastActivity',
        getDate: (row) => row.lastActivity,
    })

export const createCreatedAtFilter = <TRow extends { createdAt?: string | null }>(): FilterDefinition<TRow> =>
    createDateFilter<TRow>({
        id: 'createdAt',
        label: 'Created',
        getDate: (row) => row.createdAt,
        defaultHidden: true,
    })

export const createYearFilter = <TRow>(
    id: string,
    label: string,
    getDate: (row: TRow) => Date | string | null | undefined,
    columnId?: string,
    defaultHidden = false
): FilterDefinition<TRow> => ({
    id,
    label,
    columnId,
    defaultHidden,
    getOptions: (rows) =>
        getUniqueFilterOptions(
            rows.map((row) => {
                const year = getYear(getDate(row))

                return year
                    ? { id: String(year), label: String(year), order: year }
                    : { id: NO_DATE_OPTION_ID, label: UNKNOWN_VALUE_LABEL, order: Number.MAX_SAFE_INTEGER }
            })
        ),
    matches: (row, selectedOptionIds) => selectedOptionIds.includes(String(getYear(getDate(row)) ?? NO_DATE_OPTION_ID)),
})

export const createColumnToggleFilter = <TRow>(id: string, label: string): FilterDefinition<TRow> => ({
    id,
    label,
    columnOnly: true,
    defaultHidden: true,
    getOptions: () => [{ id: 'shown', label }],
    matches: () => true,
})

export const createMultiValueFilter = <TRow>(
    id: string,
    label: string,
    getValues: (row: TRow) => string[] | undefined,
    defaultHidden = true
): FilterDefinition<TRow> => ({
    id,
    label,
    defaultHidden,
    getOptions: (rows) =>
        getUniqueFilterOptions(
            rows.flatMap((row) => {
                const values = getValues(row) ?? []

                return values.length
                    ? values.map((value) => ({ id: value, label: value }))
                    : [{ id: UNASSIGNED_FILTER_OPTION_ID, label: UNKNOWN_VALUE_LABEL, order: Number.MAX_SAFE_INTEGER }]
            })
        ),
    matches: (row, selectedOptionIds) => {
        const values = getValues(row) ?? []

        return values.length
            ? values.some((value) => selectedOptionIds.includes(value))
            : selectedOptionIds.includes(UNASSIGNED_FILTER_OPTION_ID)
    },
})

export type NumberBand = {
    id: string
    label: string
    max?: number
}

const NO_NUMBER_OPTION_ID = 'none'

export const createNumberBandFilter = <TRow>(
    id: string,
    label: string,
    getValue: (row: TRow) => number | undefined | null,
    bands: NumberBand[],
    defaultHidden = true
): FilterDefinition<TRow> => {
    const bandIdFor = (row: TRow) => {
        const value = getValue(row)

        if (value === null || value === undefined || Number.isNaN(value)) return NO_NUMBER_OPTION_ID

        return bands.find((band) => band.max === undefined || value < band.max)?.id ?? NO_NUMBER_OPTION_ID
    }

    return {
        id,
        label,
        defaultHidden,
        getOptions: (rows) => {
            const presentIds = new Set(rows.map(bandIdFor))

            return [
                ...bands
                    .map((band, index) => ({ id: band.id, label: band.label, order: index }))
                    .filter((option) => presentIds.has(option.id)),
                ...(presentIds.has(NO_NUMBER_OPTION_ID)
                    ? [{ id: NO_NUMBER_OPTION_ID, label: UNKNOWN_VALUE_LABEL, order: bands.length }]
                    : []),
            ]
        },
        matches: (row, selectedOptionIds) => selectedOptionIds.includes(bandIdFor(row)),
    }
}
